/**
 * Ce que la fiche enfant lit et écrit à propos des classes.
 *
 * Les droits sont ceux de la base : un parent lit les centres et les classes,
 * range ses propres enfants dans une classe (`kid_classes`, une ligne par
 * année scolaire) et retire cette ligne quand l'école change. Les règles sont
 * dans `regles.ts` ; les lectures des classes viennent du module des journées
 * pédagogiques, qui les a écrites le premier.
 */

import { supabase } from '../supabase';
import { db } from '../payments/db';
import { lireClasses, lireClassesDesEnfants, rangerEnfant } from '../jpa/gateway.supabase';
import { CLASSE_HORS_LISTE, type CentreRelie, type ClasseDuCentre, type ClassesConnues } from './regles';

/** Les centres actifs et leur école : la fiche ne demande la classe qu'aux écoles reliées. */
export async function lireCentresRelies(): Promise<CentreRelie[]> {
  const { data, error } = await db
    .from('centers')
    .select('id, school_id')
    .eq('active', true)
    .eq('archived', false);
  if (error) throw error;
  return ((data ?? []) as { id: string; school_id: string | null }[]).map((c) => ({ id: c.id, school_id: c.school_id ?? null }));
}

/** Tout ce que la fiche doit savoir : les centres reliés et toutes leurs classes, fermées comprises. */
export async function lireClassesConnues(): Promise<ClassesConnues> {
  const centres = await lireCentresRelies();
  const classes = await lireClasses(centres.map((c) => c.id), true);
  return { centres, classes };
}

/** La classe d'un enfant pour une année scolaire, ou `null`. */
export async function classeDeLEnfant(kidId: string, annee: number): Promise<ClasseDuCentre | null> {
  const classes = await lireClassesDesEnfants([kidId], annee);
  return classes.get(kidId) ?? null;
}

/**
 * Enregistre la classe choisie dans la fiche. Sans classe (école sans
 * classes, école tapée à la main, classe signalée hors liste), la ligne de
 * l'année est retirée : au changement d'école, la classe ne vaut plus.
 */
export async function enregistrerClasse(
  kidId: string,
  ecoleId: string,
  ecoleActuelle: string | null,
  annee: number,
  classeId: string,
): Promise<void> {
  if (classeId && classeId !== CLASSE_HORS_LISTE) {
    await rangerEnfant(kidId, ecoleId, ecoleActuelle, annee, classeId);
    return;
  }
  const { error } = await db.from('kid_classes').delete().eq('kid_id', kidId).eq('annee_scolaire', annee);
  if (error) throw error;
}

export interface ClasseManquante {
  ecoleId: string;
  /** La classe telle que le parent la nomme. */
  classe: string;
  enfant: { prenom: string; nom: string };
}

/**
 * Prévient l'équipe qu'une classe manque à la liste d'une école : un courriel
 * part de jpa@eclosion.be vers info@eclosion.be (`signaler-classe-manquante`).
 */
export async function signalerClasseManquante(signalement: ClasseManquante): Promise<void> {
  const { data, error } = await supabase.functions.invoke('signaler-classe-manquante', { body: signalement });
  if (error) throw new Error(error.message ?? 'Le signalement n’a pas pu partir.');
  const reponse = data as { envoye?: boolean; erreur?: string } | null;
  if (reponse && reponse.envoye === false) throw new Error(reponse.erreur ?? 'Le signalement n’a pas pu partir.');
}
